import { getUserData, setUserData } from '../API';
import {
  AUDIO_BRANCH,
  CLIPPED_PHRASE_P90_PCT,
  LOW_VOLUME_SEGMENT_SHARE,
  PERVASIVE_PHRASE_P90_PCT,
  getSignalThresholds,
  isBadSignalCursorEnabled,
  buildForcedNetworkHistory,
} from './signalThresholds';
import { classifyNetworkHistory } from './networkHistorySignals';

/**
 * Answers "did the student's previous tutor activity go badly?" for the bad-signal cursor branch
 * in CursorOrchestrator (TT-991). Live and stored signals must classify identically, so both build
 * through buildAudioSignal/buildNetworkSignal, the only definitions of the normalized shape.
 *
 * THE NETWORK VERDICT IS NOT A PRIOR-ACTIVITY VERDICT ANY MORE (GT-306). hadBadNetwork is TT-1377's
 * student-level rule over the login tutor history -- cursor/networkHistorySignals, read here from
 * userData.tutorHistorySignals, launch-only. The single-activity network signal is still
 * normalized and logged, and its badCount is still compared to its floor as badCountVerdict, but
 * that is a sanity check the brief asked for and nothing reads it.
 * Tri-state: null ("unknown") is NOT bad. Synchronous — the caller is sync, so nothing may fetch.
 * The verdict also names WHICH of TT-1230's audio branches fired, because cursor-free is gated on
 * the high-precision ones and a single boolean cannot say which branch a "bad" came from, and
 * carries the normalized `signal` it read so the log can record measurements, not just verdicts.
 *
 * THE NORMALIZED SHAPE IS MEASUREMENTS, NOT VERDICTS. Every comparison happens once, in
 * classifyAudioBranches, against thresholds passed in as an argument. Both adapters therefore hand
 * the classifier the same six audio fields, and neither can arrive at a different answer for the
 * same activity — which is the only thing standing between a student and being latched into a
 * sticky, no-exit cursor mode on Monday that Tuesday's read of the same record would not have
 * chosen. isLowVolume is the one exception and says why in place.
 *
 * WHAT hadBadAudio SEES — TWO OF TT-1230's FOUR BRANCHES, AND THAT IS FINAL. Ka decided on
 * 2026-09-01: dead silence and pervasive clipping are implemented; low volume and
 * clipped + unintelligible are NOT, and are not being built. Both of those conjoin their audio
 * evidence with a per-phrase story-word overlap, so both classify null — never their audio half
 * alone. Do not add the overlap join, and do not re-stand-in for the two branches; that is the
 * mistake this file already made once. It matches TT-1230 §4's own recommendation for the sticky
 * action ("gate the sticky, high-consequence action on the high-precision branches only"), though
 * note §4 wanted the full rule for the reversible tap-to-read side, which 13/34 undershoots.
 *
 * The cost is stated rather than hidden: TT-1230 §3, "priorActivitySignals.js can only evaluate
 * pervasive clipping and dead silence, which together catch 13/34" — 13 of 34 labelled-bad
 * activities, not 33 of 34. Still true and still worth knowing: §2 specified storyWordOverlap in
 * the persisted phrase rows and #5982 shipped without it, so the stored path could not evaluate
 * those two branches even if this client did compute the join. That is a fact about the payload,
 * not an open question gating anything here.
 */

export const SIGNAL_SOURCE = Object.freeze({
  LIVE: 'live',
  STORED: 'stored',
  NONE: 'none',
});

const EMPTY_SIGNAL = Object.freeze({
  audio: null,
  network: null,
  source: SIGNAL_SOURCE.NONE,
  measuredAt: null,
  activityStatus: null,
  displayStatus: null,
});

const DAY_MS = 24 * 60 * 60 * 1000;

// ============ Normalization ============

function toFiniteOrNull(value) {
  return Number.isFinite(value) ? value : null;
}

// Only an exact boolean is a measurement. Anything else, an absent field included, is unknown.
function boolOrNull(value) {
  return typeof value === 'boolean' ? value : null;
}

// The one verdict this module normalizes rather than classifies, because the two adapters are
// handed opposite halves of it: TT-1256 persists isLowVolume as a boolean, while
// getActivityAudioMetrics() carries only the two counts ReaderManager.activityIsLowVolume raises it
// from. Derived here, at the same LOW_VOLUME_SEGMENT_SHARE, so both paths land on one field with
// one meaning. Never NaN or Infinity: both JSON-round-trip to null.
function lowVolumeFromSegmentCounts(lowRmsSegmentCount, totalSegmentCount) {
  if (!Number.isFinite(lowRmsSegmentCount) || !Number.isFinite(totalSegmentCount) || totalSegmentCount <= 0) {
    return null;
  }
  return lowRmsSegmentCount >= LOW_VOLUME_SEGMENT_SHARE * totalSegmentCount;
}

// THE definition of the normalized audio field set; both adapters go through here.
function buildAudioSignal({
  audioRuleVersion,
  flatlineMaxRunSec,
  phrasesWithAudioCount,
  pervasivelyClippedPhraseCount,
  clippedPhraseCount,
  isLowVolume,
  lowRmsSegmentCount,
  totalSegmentCount,
} = {}) {
  const audio = {
    // Which generation of the rules measured this activity. Load-bearing because neither cursor
    // mode has an automatic exit: a retune cannot un-latch a student, so the rule version that
    // latched them is the only audit trail there is.
    audioRuleVersion: toFiniteOrNull(audioRuleVersion),
    // Seconds, and the unit TT-1230's dead-silence rule is stated in (>= 30s). The only input that
    // branch has; it is compared in classifyAudioBranches rather than restated as a boolean here.
    flatlineMaxRunSec: toFiniteOrNull(flatlineMaxRunSec),
    // Denominator and numerator of TT-1230's pervasive-clipping share, at the phrase grain the rule
    // is stated in — one clipped phrase inside a long clean activity barely moves the activity-wide
    // percentile, which is why neither can be recovered from it. Counts rather than the ratio so
    // the evidence floor can see the denominator, and so "0 of 0" stays distinguishable from
    // "0 of 12".
    phrasesWithAudioCount: toFiniteOrNull(phrasesWithAudioCount),
    pervasivelyClippedPhraseCount: toFiniteOrNull(pervasivelyClippedPhraseCount),
    // The clipping half of TT-1230's clipped + unintelligible branch, at CLIPPED_PHRASE_P90_PCT.
    // Wired to no rule, on TT-1230's own terms — he calls this "an observation rather than a
    // verdict", and alone it fires on 25 of 45 labelled-GOOD activities. Carried so the dark launch
    // can measure how much the missing overlap join would actually be worth. The boolean #5953
    // calls hasClippedPhrase is this count > 0; the count is kept instead because it also says how
    // many, over how many phrases.
    clippedPhraseCount: toFiniteOrNull(clippedPhraseCount),
    // The volume half of TT-1230's low volume branch. Same reason as clippedPhraseCount: no rule
    // reads it while the overlap conjunct is missing.
    isLowVolume: boolOrNull(isLowVolume) ?? lowVolumeFromSegmentCounts(lowRmsSegmentCount, totalSegmentCount),
  };

  // An activity that measured nothing this branch can read is no signal at all, rather than a
  // signal of zeroes — the difference between "we looked, it was fine" and "we never looked", and
  // the reason a dead mic cannot classify as good audio. audioRuleVersion does not count: it is
  // provenance, present on every payload a current client writes, and evidence of nothing.
  //
  // WHICH CASE THIS ACTUALLY CATCHES, because a reviewer read more into it than it does: the
  // absence being tested is a metrics object with nothing in it, not an activity with zero scored
  // phrases. "We never looked" reaches here as a MISSING object — fromLiveMetrics with no
  // audioMetrics, fromStoredActivity with no payload — and both callers map that to audio: null
  // before this check runs. An activity that DID produce metrics reports phrasesWithAudioCount as
  // a count, and 0 is a real count: both adapters pass `phraseClippingP90.length` / the equivalent
  // filtered length, so "0 of 0 phrases" is a measurement that scored nothing, not a failure to
  // measure. The two adapters agree on that by construction, which is the property that matters;
  // nulling the count on one side would be the drift this module exists to prevent.
  const measured = Object.entries(audio)
    .some(([key, value]) => key !== 'audioRuleVersion' && value !== null);
  return measured ? audio : null;
}

// The send→ack rows TT-1256 persists, carried through verbatim for the LOG ONLY. No rule reads
// them, they are not inputs to hadBadNetwork, and they deliberately have no threshold in
// signalThresholds: TT-1257's deferral of bad network is waiting on exactly this measurement
// ("revisit once #5972's send→ack p50/p95 have a few weeks of prod"), and logging them is what
// starts that clock.
//
// The rows rather than an activity-level aggregate, because the aggregate cannot be computed
// honestly: the payload carries per-phrase percentiles, not the samples behind them, so pooling
// them would mean a percentile of percentiles — an invented statistic in the one place the analysis
// is supposed to be measuring. Bounded by TT-1230 §2's phrase counts, median 8 and max 27 per
// activity, and only rows that actually measured an ack are kept, so a payload written before the
// per-phrase network wiring adds nothing to the row.
function ackPhraseRows(phrases) {
  if (!Array.isArray(phrases)) {
    return null;
  }
  return phrases
    .filter((phrase) => Number.isFinite(phrase?.phraseIndex)
      && [phrase.ackP50Ms, phrase.ackP95Ms, phrase.ackedCount].some((value) => Number.isFinite(value)))
    .map((phrase) => ({
      phraseIndex: phrase.phraseIndex,
      ackP50Ms: toFiniteOrNull(phrase.ackP50Ms),
      ackP95Ms: toFiniteOrNull(phrase.ackP95Ms),
      ackedCount: toFiniteOrNull(phrase.ackedCount),
    }));
}

// THE definition of the normalized network field set. monitoringActive is what separates the two
// things a badNetworkCount of 0 used to mean — "monitored, never went bad" and "never monitored" —
// which closes the TODO(TT-1257) that stood here: hadBadNetwork:false is only evidence when
// monitoring was actually armed. Null (a live signal, or a record written before TT-1256 carried
// the flag) keeps the old reading, which trusts the count.
function buildNetworkSignal({ badNetworkCount, monitoringActive, phrases } = {}) {
  const count = toFiniteOrNull(badNetworkCount);
  const active = boolOrNull(monitoringActive);
  // null, not [], on the live path: "nobody measured an ack here" rather than "measured, none".
  const acks = ackPhraseRows(phrases);
  if (count === null && active === null && !acks?.length) {
    return null;
  }
  return { badNetworkCount: count, monitoringActive: active, phrases: acks };
}

// `source` is NONE whenever neither half is present, so callers test one field, not two.
// `measuredAt` is when the activity behind the signal happened -- the record's createdAt on the
// stored path, null on the live path, where "the activity that just ended" needs no timestamp.
// Only a parseable timestamp is carried; anything else is unknown, and unknown age is never stale.
// `record` carries the three fields of the activity the signal was read from that the recency
// check and the decision log need: measuredAt, and the SRS status/displayStatus of the row (null on
// the live path, where the activity has no final status yet). They ride in the envelope so every
// reader takes them from one shape, rather than reaching into the raw stored slot.
function buildNormalizedSignal(audio, network, sourceWhenPresent, { measuredAt = null, activityStatus = null, displayStatus = null } = {}) {
  const present = Boolean(audio) || Boolean(network);
  const stringOrNull = (value) => (typeof value === 'string' && value ? value : null);
  return {
    audio: audio || null,
    network: network || null,
    source: present ? sourceWhenPresent : SIGNAL_SOURCE.NONE,
    measuredAt: typeof measuredAt === 'string' && Number.isFinite(Date.parse(measuredAt)) ? measuredAt : null,
    activityStatus: stringOrNull(activityStatus),
    displayStatus: stringOrNull(displayStatus),
  };
}

// ============ Adapters ============

// Same-session adapter; either badCount spelling is taken so the activity_network_health payload
// works too. The bulky diagnostic fields stay out: they are already in the S3 logs and nothing here
// reads them.
//
// Every name on the right is getActivityAudioMetrics()'s own, taken from the merged #5953 rather
// than guessed. Reading a field it does not emit is silent — the value normalizes to null and the
// branch is skipped — so a rename upstream shows up as a branch that never fires, not as an error.
// The golden-shape and parity tests in this module's suite are the only guard.
//
// monitoringActive and the per-phrase ack rows are both forwarded when ReaderManager supplies them
// (it reads NetworkMonitoringService.monitoringArmedThisActivity and its own phraseNetworkSignals,
// the same sources TT-1256's persist path uses), so the live and stored halves of one activity
// cannot disagree about whether monitoring ran. A caller that omits them gets null -- unknown --
// never a fabricated value, because a fabricated live row would enter the log as a measurement,
// which is the one thing the TT-1257 revisit cannot afford.
export function fromLiveMetrics(audioMetrics, networkHealth) {
  const audio = audioMetrics
    ? buildAudioSignal({
      audioRuleVersion: audioMetrics.audioRuleVersion,
      flatlineMaxRunSec: audioMetrics.flatlineMaxRunSec,
      // Already cut at Speech's own PERVASIVE_PHRASE_P90_PCT / CLIPPED_PHRASE_P90_PCT, over
      // phraseClippingP90s() — the same population and the same grain fromStoredActivity cuts
      // TT-1256's phrases[] rows into. That identity is the parity guarantee.
      phrasesWithAudioCount: audioMetrics.phrasesWithAudioCount,
      pervasivelyClippedPhraseCount: audioMetrics.pervasivelyClippedPhraseCount,
      clippedPhraseCount: audioMetrics.clippedPhraseCount,
      lowRmsSegmentCount: audioMetrics.lowRmsSegmentCount,
      totalSegmentCount: audioMetrics.totalSegmentCount,
    })
    : null;

  const network = networkHealth
    ? buildNetworkSignal({
      badNetworkCount: networkHealth.badCount ?? networkHealth.badNetworkCount,
      monitoringActive: networkHealth.monitoringActive,
      // ReaderManager.phraseNetworkSignals rows, forwarded as-is. Their bulky `ackMs` sample array
      // is dropped by ackPhraseRows rather than filtered here, so there is one place that decides
      // which ack fields exist and the two adapters cannot carry different ones.
      phrases: networkHealth.phrases,
    })
    : null;

  return buildNormalizedSignal(audio, network, SIGNAL_SOURCE.LIVE);
}

/**
 * Next-day adapter: the activity comes back off the student record, carrying the payload TT-1256
 * persists as `activity.audioNetworkSignals` (see services/audioNetworkSignals.buildAudioNetworkSignals
 * on TT-1256-audio-network-signals, PR #5982):
 *
 *   { schemaVersion, audioRuleVersion, isLowVolume, flatlineMaxRunSec, badCount, monitoringActive,
 *     phrases: [{ phraseIndex, clippingP90, ackP50Ms, ackP95Ms, ackedCount }] }
 *
 * TWO VERDICTS ARE DERIVED HERE, NOT READ. The payload carries neither isFlatlined nor
 * isPervasivelyClipped — and no segment counts at all, so no totalSegmentCount, no
 * badClippedSegments and nothing to apply an activity-wide evidence floor to. What it does carry is
 * the raw per-phrase clippingP90 rows, at the same grain Speech.phraseClippingP90s() cuts for the
 * live path, so the three phrase counts are recomputed from them with the same two constants. This
 * is the whole reason the normalized shape holds measurements: the derivation happens once, in
 * classifyAudioBranches, for both adapters.
 *
 * NOT in the payload either: storyWordOverlap, which TT-1230 §2 specified. Immaterial now — low
 * volume and clipped + unintelligible are not implemented by decision, not by dependency — but it
 * is why adding them later would need a TT-1256 follow-up as well as a client-side join.
 *
 * The per-phrase rows also carry send→ack timings, which are read here for the log only. See
 * buildNetworkSignal.
 */
export function fromStoredActivity(activityRecord) {
  // Either shape. API.js stashes `{ createdAt, audioNetworkSignals }` -- the payload plus the one
  // record field the recency check needs, rather than the whole activity row full of scores and
  // phrase text -- and an actual activity record or the bare payload are accepted too. schemaVersion
  // is the discriminator that makes the fallback safe: it is set unconditionally by TT-1256's
  // buildAudioNetworkSignals, so anything without it — a pre-#5982 activity, a stray object — is
  // "no signal" rather than a payload of undefineds.
  const stored = activityRecord?.audioNetworkSignals ?? activityRecord;
  if (!Number.isFinite(stored?.schemaVersion)) {
    return { ...EMPTY_SIGNAL };
  }
  // The record's own fields, present only when the caller handed over the record (or the
  // `{ createdAt, audioNetworkSignals, activityStatus, displayStatus }` shape API.js stashes)
  // rather than the bare payload. A raw SRS row spells the status `status`; the stash spells it
  // `activityStatus`. Either is taken.
  const isRecord = Boolean(activityRecord?.audioNetworkSignals);
  const record = isRecord
    ? {
      measuredAt: activityRecord.createdAt,
      activityStatus: activityRecord.activityStatus ?? activityRecord.status ?? null,
      displayStatus: activityRecord.displayStatus ?? null,
    }
    : {};

  // Finite only, matching phraseClippingP90s()'s own filter: a phrase whose p90 was unmeasurable
  // must not enter the denominator, or the pervasive share is diluted by phrases nobody scored.
  const clippingP90s = (Array.isArray(stored.phrases) ? stored.phrases : [])
    .map((phrase) => phrase?.clippingP90)
    .filter((value) => Number.isFinite(value));

  const audio = buildAudioSignal({
    audioRuleVersion: stored.audioRuleVersion,
    flatlineMaxRunSec: stored.flatlineMaxRunSec,
    phrasesWithAudioCount: clippingP90s.length,
    pervasivelyClippedPhraseCount: clippingP90s.filter((v) => v >= PERVASIVE_PHRASE_P90_PCT).length,
    clippedPhraseCount: clippingP90s.filter((v) => v >= CLIPPED_PHRASE_P90_PCT).length,
    isLowVolume: stored.isLowVolume,
  });

  const network = buildNetworkSignal({
    badNetworkCount: stored.badCount,
    monitoringActive: stored.monitoringActive,
    phrases: stored.phrases,
  });

  return buildNormalizedSignal(audio, network, SIGNAL_SOURCE.STORED, record);
}

// ============ Classification ============

// Tri-state OR: one true is bad, all-unknown stays unknown, and only a real evaluation says false.
function anyTrue(verdicts) {
  if (verdicts.includes(true)) {
    return true;
  }
  return verdicts.includes(false) ? false : null;
}

// TT-1230's dead silence: a flat run of at least flatlineMinRunSec (30s). 100% precision — 0 false
// positives across 45 labelled-good activities — and the one branch trusted outright. No evidence
// floor: a flat run long enough to fire IS the evidence.
function classifyDeadSilence(audio, audioThresholds) {
  const run = audio?.flatlineMaxRunSec;
  const minRun = audioThresholds?.flatlineMinRunSec;
  if (!Number.isFinite(run) || !Number.isFinite(minRun)) {
    return null;
  }
  return run >= minRun;
}

// TT-1230's pervasive clipping: at least pervasivePhraseShare of the activity's phrases at
// clippingP90 >= PERVASIVE_PHRASE_P90_PCT. 67.6% precision, the second branch cursor-free trusts.
//
// The phrase floor is #5953's addition rather than TT-1230's prose, and it is not optional: without
// it a single clipped phrase is 1/1 = 100% and fires. Below the floor this answers null where the
// shipped isPervasivelyClipped answers false — too little evidence is unknown throughout this
// module, and both readings decline the branch, so no outcome differs.
function classifyPervasiveClipping(audio, audioThresholds) {
  const phrases = audio?.phrasesWithAudioCount;
  const clipped = audio?.pervasivelyClippedPhraseCount;
  const share = audioThresholds?.pervasivePhraseShare;
  const minPhrases = audioThresholds?.pervasiveMinPhrases;
  if (![phrases, clipped, share, minPhrases].every((value) => Number.isFinite(value))) {
    return null;
  }
  // `phrases === 0` is checked separately because a remote retune could set the floor to 0, and
  // 0/0 is NaN, which compares false — i.e. "we scored no phrases and they were fine".
  if (phrases < minPhrases || phrases === 0) {
    return null;
  }
  return clipped / phrases >= share;
}

// One tri-state verdict per TT-1230 branch, reported separately because only the high-precision
// ones may drive cursor-free. A missing input is null, so an unmeasurable branch never fires.
// Key order is TT-1230's own.
function classifyAudioBranches(audio, audioThresholds) {
  return {
    [AUDIO_BRANCH.DEAD_SILENCE]: classifyDeadSilence(audio, audioThresholds),
    [AUDIO_BRANCH.PERVASIVE_CLIPPING]: classifyPervasiveClipping(audio, audioThresholds),
    /*
     * TT-1230's other two branches are BOTH conjoined with per-phrase story-word overlap:
     *
     *   low volume               isLowVolume AND any phrase's overlap < 60%          42.2% precision
     *   clipped + unintelligible any phrase clippingP90 >= 1% AND that phrase's
     *                            overlap < 60%                                       37.5% precision
     *
     * Nothing computes that overlap. Not this client, and not TT-1256's stored payload. So both
     * answer null — unknown, never false, and never stood in for by the audio half alone. The two
     * stand-ins that used to sit here were `lowRmsSegmentFrac >= 0.90`, which is his rule MINUS the
     * overlap gate, and `badClippedSegmentCount >= 3`, which is the shipped isClipped flag TT-1230
     * §6 measured at Youden J 0.189 and rejected outright ("OR-ing it into the rule adds 9 false
     * positives while catching nothing new"). Both fired far wider than he measured, and below the
     * WCPM pivot they were the only route into a permanent tap-to-read.
     *
     * DECIDED 2026-09-01 BY KA — THESE TWO BRANCHES ARE NOT BEING IMPLEMENTED. Not deferred, not
     * blocked on TT-1230 or a TT-1256 follow-up: hadBadAudio ships as dead silence and pervasive
     * clipping, and hadBadAudio catches 13 of TT-1230's 34 labelled-bad activities rather than 33.
     * Do not build the client-side join, do not add `phrases[].storyWordOverlap`, and above all do
     * not restore a stand-in.
     *
     * The two keys stay in the map as explicit nulls rather than being deleted. TT-1230 defines
     * four branches and we implement two; a row logging two keys would say the rule has two, and
     * every dark-launch row would silently lose the fact that the other two were declined.
     * HIGH_PRECISION_BRANCHES is dormant for the same reason: every branch that can fire is
     * already in it.
     */
    [AUDIO_BRANCH.LOW_VOLUME]: null,
    [AUDIO_BRANCH.CLIPPED_UNINTELLIGIBLE]: null,
  };
}

// TT-1230's rule verbatim: bad audio is ANY of the four branches, and nothing else. The six
// placeholder threshold rules that used to be OR-ed in beside them are gone — §5 swept 35
// feature/percentile combinations against out-of-sample thresholds for a best gain of +0.008
// Youden J and concluded nothing beyond clipping and db improves the detector, and severeSegFrac,
// allZerosSegmentFrac and the two static fractions were exactly that rejected set.
function classifyAudio(audio, audioThresholds) {
  const audioBranches = classifyAudioBranches(audio, audioThresholds);
  return { hadBadAudio: anyTrue(Object.values(audioBranches)), audioBranches };
}

// TT-1257's finished-story episode count against its floor. SANITY CHECK ONLY (GT-306): this
// verdict is logged as badCountVerdict and feeds nothing — hadBadNetwork is TT-1377's rule over the
// login history, computed in networkHistorySignals, because badCount is written at story end and
// cannot see a quit. A count taken while monitoring was never armed says nothing about the
// network, so it answers unknown rather than false. network.phrases is NOT read here.
function classifyBadCount(network, networkThresholds) {
  if (network?.monitoringActive === false) {
    return null;
  }
  const count = network?.badNetworkCount;
  const threshold = networkThresholds?.badNetworkCount;
  if (!Number.isFinite(count) || !Number.isFinite(threshold)) {
    return null;
  }
  return count >= threshold;
}

// The SINGLE-ACTIVITY verdicts: TT-1230's audio rule and TT-1257's badCount sanity check, over one
// normalized signal. hadBadNetwork is deliberately NOT here — it is a verdict over the student's
// login history, not over one activity, and lives in networkHistorySignals.classifyNetworkHistory.
// Thresholds are an ARGUMENT, never a global: importing getSignalThresholds here would make every
// assertion in this module's suite a restatement of today's numbers.
export function classifySignals(normalized, thresholds) {
  const { hadBadAudio, audioBranches } = classifyAudio(normalized?.audio, thresholds?.audio);
  return {
    hadBadAudio,
    badCountVerdict: classifyBadCount(normalized?.network, thresholds?.network),
    audioBranches,
  };
}

// ============ Entry point ============

// Discriminating on `source` is the seam that lets one userData field carry both the signal this
// session stashed and the raw activity record API.js reads back off the student record.
function isNormalizedActivitySignal(value) {
  return Boolean(value)
    && (value.source === SIGNAL_SOURCE.LIVE || value.source === SIGNAL_SOURCE.STORED);
}

// Write side of the seam: called at tutor activity end, read back by the next activity. ALWAYS
// writes, empty case included — clearing is what keeps "prior" meaning the LAST tutor activity.
export function recordActivitySignalsForNextActivity(audioMetrics, networkHealth) {
  if (!isBadSignalCursorEnabled()) {
    return null;
  }
  const signal = fromLiveMetrics(audioMetrics, networkHealth);
  const stashed = signal.source === SIGNAL_SOURCE.NONE ? null : signal;
  setUserData('priorActivitySignals', stashed);
  return stashed;
}

// Built through classifySignals so the "no opinion" answer cannot drift from the real shape when a
// branch is added, and so each caller gets its own branch map to hold.
function noAudioVerdict() {
  return { ...classifySignals(), source: SIGNAL_SOURCE.NONE, signal: null, stale: null };
}

// The network half's "no opinion": no history was stashed (or the gate is closed), so nothing was
// evaluated. Distinct from a history that WAS evaluated and counted zero sessions, which returns a
// summary with sessionCount 0 — the log needs to tell those apart.
const NO_NETWORK_VERDICT = Object.freeze({ hadBadNetwork: null, networkBranches: null, networkHistory: null });

function noVerdict() {
  return { ...noAudioVerdict(), ...NO_NETWORK_VERDICT };
}

/**
 * GT-306: TT-1377's rule over the login tutor history. LAUNCH-ONLY. The rows are what
 * API.saveUserData stashed as userData.tutorHistorySignals when the student logged in, and
 * nothing in-session rewrites them — the activity that just ended is not appended, so the verdict
 * is the same on every activity of the session. (recordActivitySignalsForNextActivity still
 * forwards the live network rows, but only into the single-activity signal the log records.)
 *
 * ?forceHighRttNetwork=true substitutes a fabricated history (buildForcedNetworkHistory) and is
 * reported as such, so QA can reach the network outcomes without a slow network across two days.
 */
function classifyNetworkFromHistory(thresholds) {
  const forced = buildForcedNetworkHistory(thresholds?.network);
  const rows = forced ?? getUserData()?.tutorHistorySignals;
  if (!Array.isArray(rows)) {
    return { ...NO_NETWORK_VERDICT };
  }
  // No age bound on these rows: the window IS the login read (Hriday, GT-306). The audio
  // signal's maxSignalAgeDays stays the audio signal's.
  const { hadBadNetwork, networkBranches, summary } = classifyNetworkHistory(rows, thresholds?.network);
  return {
    hadBadNetwork,
    networkBranches,
    networkHistory: { ...summary, forced: Boolean(forced) },
  };
}

// A STORED signal older than thresholds.maxSignalAgeDays is stale. Live signals never are, an
// unknown measuredAt cannot be judged (and is not refused -- unknown age is not old age), and a
// missing bound applies no bound. Inclusive at the boundary: exactly N days old is still fresh.
function isStaleSignal(normalized, thresholds) {
  if (normalized.source !== SIGNAL_SOURCE.STORED || normalized.measuredAt === null) {
    return false;
  }
  const maxAgeDays = thresholds?.maxSignalAgeDays;
  if (!Number.isFinite(maxAgeDays)) {
    return false;
  }
  return Date.now() - Date.parse(normalized.measuredAt) > maxAgeDays * DAY_MS;
}

// The single entry point CursorOrchestrator calls. Every unknown — gate off, no prior activity, no
// metrics, no thresholds — returns all-null / source NONE, which the policy reads as "no opinion".
//
// Two independent halves (GT-306). The AUDIO half reads the single prior activity — the live stash
// or the newest stored row — and can be absent or stale on its own. The NETWORK half reads the
// login tutor history and is evaluated whenever that stash exists, including when the newest
// activity carried no payload (a story quit before its first phrase) or is too old to classify:
// a student whose last story was abandoned can still have a chronically slow network across the
// rows the login read returned.
export function getPriorActivitySignals() {
  if (!isBadSignalCursorEnabled()) {
    return noVerdict();
  }

  const thresholds = getSignalThresholds();
  const network = classifyNetworkFromHistory(thresholds);

  const priorActivitySignals = getUserData()?.priorActivitySignals;
  if (!priorActivitySignals) {
    return { ...noAudioVerdict(), ...network };
  }

  const normalized = isNormalizedActivitySignal(priorActivitySignals)
    ? priorActivitySignals
    : fromStoredActivity(priorActivitySignals);

  if (normalized.source === SIGNAL_SOURCE.NONE) {
    return { ...noAudioVerdict(), ...network };
  }

  // A stale stored signal is not classified: every verdict is unknown, which the policy reads as
  // "no opinion". The measurements still go out under `signal` so the log shows what was declined
  // and when it was measured -- the analysis needs to see the rows the bound removed.
  if (isStaleSignal(normalized, thresholds)) {
    return { ...classifySignals(), ...network, source: normalized.source, signal: normalized, stale: true };
  }

  const { hadBadAudio, badCountVerdict, audioBranches } = classifySignals(normalized, thresholds);
  // `signal` is the normalized envelope these verdicts were computed FROM, carried out so the
  // dark-launch log can record the measurements rather than only the conclusions. Returned rather
  // than re-derived at the log site: whatever was classified is what must be logged, or a retune
  // cannot be checked against the rows that motivated it. It adds inputs, never a verdict.
  return {
    hadBadAudio,
    badCountVerdict,
    audioBranches,
    ...network,
    source: normalized.source,
    signal: normalized,
    stale: false,
  };
}
